<template>
  <PreparationStatusCompact
    v-if="compact"
    :preparation="preparation"
    :preparation-loading="preparationLoading"
    :tracking-prefix="trackingPrefix"
    @toggle-prepared="togglePrepared"
  />

  <div
    v-else-if="preparation"
    v-tooltip.placementBottom.html.textLeft="formattedPreparationTime"
    class="
      tw-px-2
      tw-bg-slate-50
      tw-h-full
      tw-flex
      tw-items-center
      tw-group
      tw-relative
      preparation-status-container
    "
    data-testid="reconciliation-preparation-status"
  >
    <WorkflowIconPrepared class="tw-mr-1 tw-relative" />

    <span class="tw-relative tw-z-10 tw-max-w-[300px] tw-truncate">
      {{ I18n.t('reconciliation_preparation.prepared') }}
    </span>

    <UnmarkButton
      class="tw-z-10"
      :tooltip="I18n.t('reconciliation_preparation.unmark_as_prepared')"
      :background="'tw-bg-[linear-gradient(90deg,_rgba(248,250,252,0)_0%,_rgba(248,250,252,1)_60%,_rgba(248,250,252,1)_100%)]'"
      :tracking-prefix="trackingPrefix"
      :tracking-action="'unmark-as-prepared'"
      :disabled="preparationLoading"
      @on-mouse-enter="onUnmarkButtonMouseEnter"
      @on-mouse-leave="onUnmarkButtonMouseLeave"
      @unmark="togglePrepared(false)"
    />
  </div>

  <div
    v-else
    class="
      tw-relative tw-group tw-z-10
      preparation-status-container preparation-status-container--white
    "
    data-testid="reconciliation-preparation-status"
  >
    <div
      v-if="hasAnyUpToDateReview"
      class="
        tw-px-2
        tw-h-full
        tw-flex
        tw-items-center
        tw-z-10
        tw-relative
        tw-truncate
        tw-bg-sf-snow-lighter
      "
    >
      {{ I18n.t('reconciliation_preparation.not_marked_as_prepared') }}
    </div>

    <SfButtonInverted
      class="
        !tw-border-0
        tw-shadow-none tw-rounded-none
        hover:tw-border-0 hover:tw-shadow-none
        tw-h-full tw-text-center
        group-hover:tw-visible
        tw-z-20
        !tw-text-inherit
      "
      variant="secondary"
      seamless
      :disabled="preparationLoading"
      :data-heap="`${trackingPrefix}-review-status-mark-as-prepared`"
      :class="markAsPreparedClass"
      @click.stop.prevent="togglePrepared(true)"
    >
      {{ I18n.t('reconciliation_preparation.mark_as_prepared') }}
    </SfButtonInverted>
  </div>
</template>
<script lang="ts" setup>
import type { TemplatePreparation } from '@app/application/workflows/types'
import { formatPreparationUser } from '@app/application/workflows/utils/format-preparation-and-review'
import { computed, ref } from 'vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import UnmarkButton from './unmark-button.vue'
import WorkflowIconPrepared from '@app/shared/assets/icons/svg/icon-prepared.svg?component'
import PreparationStatusCompact from './preparation-status-compact.vue'
import SfButtonInverted from '@app/shared/vue/components/sf-button/sf-button-inverted.vue'

interface Props {
  preparation: TemplatePreparation | null
  preparationLoading: boolean
  hasAnyUpToDateReview: boolean
  compact: boolean
  trackingPrefix: string
}

const props = defineProps<Props>()

const emit = defineEmits<{ 'toggle-prepared': [boolean] }>()

const I18n = useI18n()

const hasTooltip = ref(true)

const formattedPreparationTime = computed(() => {
  if (!props.preparation || !hasTooltip.value) {
    return ''
  }

  return `${I18n.t(
    'reconciliation_preparation.marked_as_prepared_by'
  )} <br /> ${formatPreparationUser(props.preparation)}`
})

const markAsPreparedClass = computed(() => {
  if (props.hasAnyUpToDateReview) {
    return 'tw-invisible tw-absolute tw-left-0 tw-top-0 tw-right-0 tw-bottom-0'
  }
  return 'tw-visible tw-relative'
})

function togglePrepared(prepared: boolean) {
  emit('toggle-prepared', prepared)
}

function onUnmarkButtonMouseEnter() {
  hasTooltip.value = false
}
function onUnmarkButtonMouseLeave() {
  hasTooltip.value = true
}
</script>
